import { useMemo } from 'react'

import cn from 'classnames'
import { CircleDashed, Loader2 } from 'lucide-react'

import { CheckCircleIcon } from '@heroicons/react/24/outline'

import { ChainId, getGasTokenSymbol } from '@src/configs/chainInfo'
import { StepNames } from '@src/enums'
import { LiquiditySource } from '@src/enums'
import { IExchangeData } from '@src/types/exchange'
import { isUnWrapOnly, isWrapOnly } from '@src/utils/exchangeUtils'

interface StepProps {
    number: number
    title: string
    status: 'pending' | 'active' | 'completed' | 'waiting'
    isLastStep?: boolean
}

interface TransactionStepsProps {
    currentStep: StepNames
    sellData: IExchangeData
    buyData: IExchangeData
    liquiditySource: LiquiditySource
}

const Step = ({ number, title, status, isLastStep }: StepProps) => {
    return (
        <div className='flex items-start gap-3'>
            <div className='relative'>
                {((): JSX.Element => {
                    let element: JSX.Element = <></>

                    if (status === 'completed') {
                        element = <CheckCircleIcon className='h-8 w-8 text-green-500' />
                    } else {
                        if (status === 'active') {
                            element = (
                                <div className='relative'>
                                    <div className='flex h-8 w-8 items-center justify-center rounded-full border-2 border-[#2A2B2E]'>
                                        {number}
                                    </div>
                                </div>
                            )
                        } else if (status === 'pending') {
                            element = (
                                <div className='relative'>
                                    <Loader2 className='text-mach-accent-foreground h-8 w-8 animate-spin' />
                                    <span className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-sm'>
                                        {number}
                                    </span>
                                </div>
                            )
                        } else {
                            element = (
                                <div className='relative'>
                                    <CircleDashed className='h-8 w-8 text-mach-sub-text' />
                                    <span className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-sm text-mach-sub-text'>
                                        {number}
                                    </span>
                                </div>
                            )
                        }
                    }

                    return element
                })()}
                {!isLastStep && <div className='absolute left-4 top-8 mt-[-1px] h-7 w-0.5 bg-[#2A2B2E]' />}
            </div>
            <div className='flex flex-col gap-1 pb-6'>
                <span
                    className={cn(
                        'text-base font-medium',
                        status === 'completed'
                            ? 'text-green-500'
                            : status === 'waiting'
                              ? 'text-mach-sub-text'
                              : 'text-mach-accent-foreground'
                    )}
                >
                    {title}
                </span>
                <span className='text-sm text-mach-sub-text'>
                    {status === 'completed'
                        ? 'Completed'
                        : status === 'active'
                          ? 'In-Progress'
                          : status === 'pending'
                            ? 'Processing...'
                            : 'Waiting'}
                </span>
            </div>
        </div>
    )
}

const getStepStatus = (stepName: StepNames, currentStep: StepNames): 'pending' | 'active' | 'completed' | 'waiting' => {
    type StepOrderType = {
        [key in StepNames]?: number
    }

    const stepOrder: StepOrderType = {
        [StepNames.UNSTARTED]: 0,
        [StepNames.DEPOSIT_WETH]: 1,
        [StepNames.DEPOSIT_WETH_PENDING]: 1.5,
        [StepNames.APPROVE_TOKENS]: 2,
        [StepNames.APPROVE_TOKENS_PENDING]: 2.5,
        [StepNames.CONFIRM_SWAP]: 3,
        [StepNames.CONFIRM_SWAP_PENDING]: 3.5,
        [StepNames.MATCHING]: 4,
        [StepNames.MATCH_CREATED]: 5,
        [StepNames.MATCH_EXECUTED]: 6,
        [StepNames.SWITCH_NETWORKS]: 0,
        [StepNames.UNWRAP_WETH]: 7,
        [StepNames.UNWRAP_WETH_PENDING]: 7.5,
    }

    const currentStepNumber = stepOrder[currentStep] ?? 0
    const thisStepNumber = stepOrder[stepName] ?? 0

    if (currentStep.includes('PENDING') && thisStepNumber === Math.floor(currentStepNumber)) {
        return 'pending'
    }
    if (currentStepNumber === thisStepNumber) {
        return 'active'
    }
    if (currentStepNumber > thisStepNumber) {
        return 'completed'
    }
    return 'waiting'
}

export default function TransactionSteps({ currentStep, sellData, buyData, liquiditySource }: TransactionStepsProps) {
    const isNativeToken = sellData.token?.native
    const isSolanaCCTP = sellData.chainId === ChainId.Solana && liquiditySource === LiquiditySource.cctpDirect
    const isJupiterSwap = liquiditySource === LiquiditySource.jupiter

    const steps = useMemo(() => {
        const stepsToShow: {
            number: number
            title: string
            stepName: StepNames
        }[] = []

        if (isWrapOnly(sellData, buyData)) {
            const wrappedSymbol = `W${sellData.token?.symbol}`
            stepsToShow.push({
                number: 1,
                title: `Wrap ${sellData.token?.symbol} → ${wrappedSymbol}`,
                stepName: StepNames.DEPOSIT_WETH,
            })
        } else if (isUnWrapOnly(sellData, buyData)) {
            stepsToShow.push({
                number: 1,
                title: `Unwrap ${sellData.token?.symbol} → ${getGasTokenSymbol(+sellData.chainId)}`,
                stepName: StepNames.UNWRAP_WETH,
            })
        } else {
            // Skip approval steps for Jupiter swaps and CCTP, showing only the execution step
            if (!isSolanaCCTP && !isJupiterSwap) {
                if (isNativeToken) {
                    const wrappedSymbol = `W${sellData.token?.symbol}`
                    stepsToShow.push({
                        number: 1,
                        title: `Wrap ${sellData.token?.symbol} → ${wrappedSymbol}`,
                        stepName: StepNames.DEPOSIT_WETH,
                    })

                    stepsToShow.push({
                        number: 2,
                        title: `Approve ${wrappedSymbol}`,
                        stepName: StepNames.APPROVE_TOKENS,
                    })
                } else {
                    stepsToShow.push({
                        number: 1,
                        title: `Approve ${sellData.token?.symbol || 'Token'}`,
                        stepName: StepNames.APPROVE_TOKENS,
                    })
                }
            }

            stepsToShow.push({
                number: stepsToShow.length + 1,
                title: 'Swap Tokens',
                stepName: StepNames.CONFIRM_SWAP,
            })
        }

        return stepsToShow
    }, [isNativeToken, sellData.token?.symbol, isSolanaCCTP])

    return (
        <div className='flex flex-col pt-4'>
            {steps.map((step, index) => (
                <Step
                    key={step.stepName}
                    number={step.number}
                    title={step.title}
                    status={getStepStatus(step.stepName, currentStep)}
                    isLastStep={index === steps.length - 1}
                />
            ))}
        </div>
    )
}
